"use client";

import { useState, useMemo, useLayoutEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon, Menu01Icon, Search01Icon, Logout01Icon, ArrowLeftDoubleIcon, ArrowRightDoubleIcon,
} from "@hugeicons/core-free-icons";
import { assets } from "@/assets/assets";
import { SECTIONS, BOTTOM_ITEMS, isActiveRoute, type NavItem } from "./nav-config";
import { getStoredUser, getStoredValue, logout as serviceLogout } from "../../utils/service";

const COLLAPSE_KEY = "bowerbird_sidebar_collapsed";
const EXPANDED_W = 256;
const COLLAPSED_W = 76;

const useUserRole = () =>
  useMemo(() => {
    const stored = getStoredUser();
    return (stored?.role || getStoredValue("userRole") || "user").toLowerCase();
  }, []);

function SidebarLink({ item, active, collapsed }: { item: NavItem; active: boolean; collapsed?: boolean }) {
  return (
    <Link
      to={item.path}
      title={collapsed ? item.title : undefined}
      className={`group relative flex items-center gap-3 rounded-xl text-sm transition-all duration-200 ${
        collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"
      } ${active ? "bg-primary text-white font-medium shadow-sm" : "text-gray-600 hover:text-gray-900 hover:bg-white/60"}`}
    >
      <HugeiconsIcon icon={item.icon} size={16} className="shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
    </Link>
  );
}

function SectionLabel({ children, collapsed }: { children: string; collapsed: boolean }) {
  if (collapsed) return <div className="mx-3 my-3 border-t border-primary/20" />;
  return <p className="px-3 pt-4 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">{children}</p>;
}

function SidebarContent({ collapsed, user, onToggleCollapse, onNavigate }: {
  collapsed: boolean; user: SidebarUser; onToggleCollapse?: () => void; onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const role = useUserRole();
  const visible = (items: NavItem[]) => items.filter((n) => n.roles.includes(role));
  const openSearch = () => window.dispatchEvent(new Event("open-search"));

  return (
    <div className="flex flex-col w-full h-full p-3 gap-3" onClick={onNavigate}>
      {/* Brand card */}
      <div className={`flex items-center bg-white rounded-2xl shadow-sm shrink-0 ${collapsed ? "justify-center h-14" : "justify-between px-3 h-14"}`}>
        <img
          src={collapsed ? assets.SideBarClosed : assets.SideBarOpen}
          alt="Bowerbird"
          className={`w-auto object-contain shrink-0 ${collapsed ? "h-7" : "h-8"}`}
        />
        {!collapsed && onToggleCollapse && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            className="hidden lg:flex p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-primary/10 transition-colors"
            title="Collapse sidebar"
          >
            <HugeiconsIcon icon={ArrowLeftDoubleIcon} size={15} />
          </button>
        )}
      </div>

      {/* Search */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); openSearch(); }}
        title={collapsed ? "Search" : undefined}
        className={`flex items-center gap-2 h-10 rounded-xl bg-white/60 hover:bg-white text-gray-500 transition-colors shrink-0 ${
          collapsed ? "justify-center" : "px-3"
        }`}
      >
        <HugeiconsIcon icon={Search01Icon} size={15} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left text-xs">Search</span>
            <span className="flex gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-primary/10 text-[10px] text-gray-500">⌘</kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-primary/10 text-[10px] text-gray-500">K</kbd>
            </span>
          </>
        )}
      </button>

      {/* Grouped navigation */}
      <nav className="flex-1 overflow-y-auto no-scrollbar -mx-1 px-1">
        {SECTIONS.map((section, i) => {
          const items = visible(section.items);
          if (!items.length) return null;
          return (
            <div key={section.label} className={i > 0 ? "mt-2 border-t border-primary/20" : ""}>
              <SectionLabel collapsed={collapsed && i === 0 ? false : collapsed}>{section.label}</SectionLabel>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <SidebarLink key={item.path} item={item} active={isActiveRoute(item.path, pathname)} collapsed={collapsed} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Bottom links */}
      <div className="space-y-0.5 shrink-0">
        {visible(BOTTOM_ITEMS).map((item) => (
          <SidebarLink key={item.path} item={item} active={isActiveRoute(item.path, pathname)} collapsed={collapsed} />
        ))}
        {collapsed && onToggleCollapse && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            className="w-full flex items-center justify-center py-2.5 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-white/60 transition-colors"
            title="Expand sidebar"
          >
            <HugeiconsIcon icon={ArrowRightDoubleIcon} size={15} />
          </button>
        )}
      </div>

      {/* User card */}
      <div className={`flex items-center bg-white rounded-2xl shadow-sm shrink-0 ${collapsed ? "justify-center h-14" : "gap-3 p-3"}`}>
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shrink-0" title={collapsed ? user.name : undefined}>
          <span className="text-xs font-semibold text-white">{user.initials}</span>
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate">{user.name}</p>
              <p className="text-[11px] text-gray-500 truncate capitalize">{user.role}</p>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); serviceLogout(); }}
              className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition-colors"
              title="Sign out"
            >
              <HugeiconsIcon icon={Logout01Icon} size={15} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

interface SidebarUser { name: string; email: string; role: string; initials: string }

export default function AppSidebar({ mobileOpen, onCloseMobile, user }: { mobileOpen: boolean; onCloseMobile: () => void; user: SidebarUser }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });

  const toggleCollapse = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // --sidebar-w is the space the page must leave for the desktop sidebar (0 below lg).
  useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () =>
      document.documentElement.style.setProperty(
        "--sidebar-w",
        mq.matches ? `${collapsed ? COLLAPSED_W : EXPANDED_W}px` : "0px"
      );
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [collapsed]);

  return (
    <>
      <aside
        className="hidden lg:flex fixed top-0 left-0 bottom-0 z-40 bg-transparent transition-[width] duration-200"
        style={{ width: collapsed ? COLLAPSED_W : EXPANDED_W }}
      >
        <SidebarContent collapsed={collapsed} user={user} onToggleCollapse={toggleCollapse} />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-black/60 z-[9995]"
              onClick={onCloseMobile}
            />
            <motion.div
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="lg:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] z-[9996] bg-white"
            >
              <div className="absolute inset-0 bg-primary/20 pointer-events-none" />
              <div className="relative flex justify-end px-3 pt-3">
                <button
                  type="button"
                  onClick={onCloseMobile}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-900 hover:bg-primary/10 transition-colors"
                >
                  <HugeiconsIcon icon={Cancel01Icon} size={16} />
                </button>
              </div>
              <div className="relative h-[calc(100%-2.75rem)]">
                <SidebarContent collapsed={false} user={user} onNavigate={onCloseMobile} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export function MobileSidebarTrigger({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lg:hidden p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-primary/10 transition-colors"
      title="Open menu"
    >
      <HugeiconsIcon icon={Menu01Icon} size={18} />
    </button>
  );
}
